import React, { useState, useEffect } from 'react'
import { Amplify, Auth } from 'aws-amplify'
import { BrowserRouter as Router, Route } from 'react-router-dom'
import Routes from './Routes'
import config from './config'
import TheHeader from 'components/common/TheHeader'
import TheEnvironmentBar from 'components/common/TheEnvironmentBar'
import TheFooter from 'components/common/TheFooter'
import LicenseAgreementModal from 'components/_LicenseAgreement/LicenseAgreementModal'
import TheToTop from 'components/common/TheToTop'
import ServiceWorkerUpdateModal from './ServiceWorkerUpdateModal'
import translationsEn from './translations/en.json'
import translationsCs from './translations/cs.json'
import translationsDe from './translations/de.json'
import translationsEs from './translations/es.json'
import translationsFi from './translations/fi.json'
import translationsFr from './translations/fr.json'
import translationsHu from './translations/hu.json'
import translationsIt from './translations/it.json'
import translationsJp from './translations/jp.json'
import translationsKo from './translations/ko.json'
import translationsNl from './translations/nl.json'
import translationsPl from './translations/pl.json'
import translationsPt from './translations/pt.json'
import translationsRu from './translations/ru.json'
import translationsZhHant from './translations/zh-HANT.json'
import translationsZh from './translations/zh.json'
import translationsSe from './translations/se.json'
import translationsTr from './translations/tr.json'
import translationsVi from './translations/vi.json'
import { IntlProvider } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import ReactGA from 'react-ga'
import { setCurrentUser } from './redux/actions/currentUserActions'
import Offline from 'components/_Offline/Offline'
import OfflineBar from 'components/_Offline/OfflineBar'
import { checkFuzzyBoolean, isInternalUser, isUserAdmin } from 'components/formcomponents/utils/userUtils'
import _ from 'lodash'

const languageTranslations = {
  en: translationsEn,
  cs: translationsCs,
  de: translationsDe,
  es: translationsEs,
  fi: translationsFi,
  fr: translationsFr,
  hu: translationsHu,
  it: translationsIt,
  jp: translationsJp,
  ko: translationsKo,
  nl: translationsNl,
  pl: translationsPl,
  pt: translationsPt,
  ru: translationsRu,
  'zh-HANT': translationsZhHant,
  zh: translationsZh,
  vi: translationsVi,
  se: translationsSe,
  tr: translationsTr

}

const AWS_REGION = 'eu-west-1'

const App = () => {
  const dispatch = useDispatch()

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isUserExternal, setIsUserExternal] = useState(false)
  const [user, setUser] = useState(null)
  const [isLicenseAgreementAccepted, setIsLicenseAgreementAccepted] = useState(false)

  const currentLanguage = useSelector(state => state.language)
  const connection = useSelector(state => state.connection)
  const currentUser = useSelector(state => state.currentUser)

  useEffect(() => {
    Amplify.configure({
      Auth: config.COGNITO_AUTH,
      Storage: {
        AWSS3: {
          bucket: config.BUCKET,
          region: AWS_REGION,
        },
      },
    })

    const initializeGA = () => {
      const environment = process.env.REACT_APP_STAGE
      if (environment === 'stage') {
        console.log('Initialized Google analytics for stage')
        ReactGA.initialize('UA-30046188-41')
      } else if (environment === 'prod') {
        console.log('Initialized Google analytics for prod')
        ReactGA.initialize('UA-30046188-41')
      } else if (!environment) {
        console.log('Initialized Google analytics for dev')
        ReactGA.initialize('UA-113741941-2')
      }
      ReactGA.pageview('/dashboard')
    }

    const fetchCurrentUser = async () => {
      try {
        await Auth.currentSession()
        setIsAuthenticated(true)
        const userAuth = await Auth.currentAuthenticatedUser()
        const isExternal = userAuth.attributes['custom:external']?.toLocaleLowerCase() === 'true'
        setIsUserExternal(isExternal)
        setUser(userAuth.attributes)
        dispatch(setCurrentUser(userAuth.attributes))
      } catch (e) {
        if (e !== 'No current user') {
          console.log(e)
        }
      }
    }

    initializeGA()
    fetchCurrentUser()
  }, [dispatch, isAuthenticated])

  useEffect(() => {
    if (!currentUser) return

    if (_.isEmpty(currentUser)) return

    if (isInternalUser(currentUser['custom:external']) || isUserAdmin(currentUser['custom:administrator'])) {
      setIsLicenseAgreementAccepted(true)
      return
    }

    getCurrentSessionUser()
  }, [currentUser])

  async function getCurrentSessionUser() {
    try {
      await Auth.currentSession()
      const userAuth = await Auth.currentAuthenticatedUser()
      const userAttributes = userAuth.attributes

      const hasAcceptedLicense = checkFuzzyBoolean(userAttributes['custom:LA_accepted'])
      setIsLicenseAgreementAccepted(hasAcceptedLicense)
    } catch (e) {
      console.log(e)
    }
  }

  const getMessages = () => {
    const currentLangMessages = languageTranslations[currentLanguage] || {};
    // Strip empty strings so missing translations fall back to English
    const filteredLang = Object.fromEntries(
      Object.entries(currentLangMessages).filter(([, v]) => v !== '')
    );
    return Object.assign({}, languageTranslations['en'], filteredLang);
  }

  const childProps = {
    isAuthenticated,
    userHasAuthenticated: setIsAuthenticated,
    setUserExternal: setIsUserExternal,
    setUser,
    isUserExternal,
    user,
  }

  const notcase = /^(?!.*(\/case)).*$/

  const showLicenseAgreementModal = !_.props?.currentUser.isEmpty() && isUserExternal && !isLicenseAgreementAccepted && connection

  return (
    <>
        <Offline />
        {showLicenseAgreementModal &&
          <LicenseAgreementModal
            showModal={showLicenseAgreementModal}
          />
        }
        <Router exact>
          <IntlProvider
            locale={currentLanguage}
            defaultLocale={'en'}
            messages={getMessages()}
          >
            <ServiceWorkerUpdateModal />
            <div id='app' className='App page-layout'>
              <OfflineBar />
              <TheEnvironmentBar />
              <Route
                path={notcase}
                render={() => (
                  <TheHeader
                    childProps={childProps}
                    isDisconnected={!connection}
                  />
                )}
              />
              <Routes childProps={childProps} />
              <Route
                path={notcase}
                render={() => (
                  <TheFooter isUserExternal={isUserExternal} />
                )}
              />
              <TheToTop />
            </div>
          </IntlProvider>
        </Router>
    </>
  )
}

export default App